import type { Identity } from '../../shared/types';
import Hero from './Hero';
import HomeGrid, { type HomeTileDef } from './HomeGrid';
import ModuleTiles, { TILE_STYLES, type Tile } from './ModuleTiles';
import AllowanceCard from './cards/AllowanceCard';
import KidChoresCard from './cards/KidChoresCard';
import DinnerCard from './cards/DinnerCard';
import UpNextCard from './cards/UpNextCard';
import AskCard from './cards/AskCard';
import VerseCard from '../verse/VerseCard';
import MessageBoardCard from '../messages/MessageBoardCard';
import { KidMoneyCard } from '../kidmoney/KidMoneyCard';
import WeatherCard from '../weather/WeatherCard';
import BirthdaysCard from '../birthdays/BirthdaysCard';
import { useResource } from './useResource';
import { addDays, isoDate, localWeekStart } from './dates';
import {
  choresFor,
  colorFor,
  dinnerFor,
  fetchAllowance,
  fetchChores,
  fetchEvents,
  fetchNotifications,
  fetchPlan,
  unreadCount,
  upcoming,
} from './homeData';

/**
 * The kid view. NO household money — not spend, not caps, not the health score,
 * not a "you can't see this" placeholder. The only money here is their own
 * allowance, which they earned.
 *
 * Every adult-only endpoint is simply never called: the Worker would 403 a child
 * by design, and a card that renders a 403 is a broken card. Nothing on this
 * screen touches /transactions, /insights, /family, or /vault.
 */
export default function KidHome({ identity }: { identity: Identity }) {
  const today = isoDate();
  const week = localWeekStart();
  const from = today;
  const to = isoDate(addDays(new Date(), 8));
  const memberId = identity.memberId;

  const chores = useResource('chores', fetchChores);
  const allowance = useResource(
    `allowance:${memberId}`,
    () => fetchAllowance(memberId as number),
    memberId !== null
  );
  const plan = useResource(week, () => fetchPlan(week));
  const events = useResource(`${from}|${to}`, () => fetchEvents(from, to));
  const notifications = useResource('notifications', fetchNotifications);

  // Children can't read the adult-gated roster, so their color comes from
  // Identity.color — their own family_members.color, carried by /api/me.
  const color = colorFor(identity, null);

  const mine = choresFor(chores.data, memberId);
  const todaysChores = mine.filter((c) => c.cadence === 'daily');
  const dinner = dinnerFor(plan.data, today)?.meal ?? null;
  const nextEvent = upcoming(events.data, 1)[0] ?? null;
  const unread = unreadCount(notifications.data);

  // Same rule as the adult hub: a read that failed is not a zero. A kid told
  // "nothing to do" because /chores 404'd is a kid who doesn't get paid.
  const tiles: Tile[] = [
    {
      to: '/birthdays',
      label: 'Birthdays',
      hint: 'Who’s coming up',
      ...TILE_STYLES.birthdays,
    },
    {
      to: '/reminders',
      label: 'Reminders',
      hint: 'Nudges for the house',
      ...TILE_STYLES.reminders,
    },
    {
      to: '/ask',
      label: 'Ask',
      hint: 'A little help',
      ...TILE_STYLES.ask,
    },

    {
      to: '/chores',
      label: 'Chores',
      hint: chores.error
        ? "Couldn't load your chores"
        : mine.length > 0
          ? `${mine.length} on your list`
          : 'Nothing to do',
      ...TILE_STYLES.chores,
    },
    {
      to: '/meals',
      label: 'Meals',
      hint: plan.error
        ? "Couldn't load meals"
        : dinner
          ? `Tonight: ${dinner.name}`
          : 'Nothing planned tonight',
      ...TILE_STYLES.meals,
    },
    {
      to: '/calendar',
      label: 'Calendar',
      hint: events.error
        ? "Couldn't load the calendar"
        : nextEvent
          ? `Next: ${nextEvent.title}`
          : 'Nothing this week',
      ...TILE_STYLES.calendar,
    },
    {
      to: '/location',
      label: 'Where',
      hint: 'See the family map',
      ...TILE_STYLES.location,
    },
    {
      to: '/notifications',
      label: 'Alerts',
      hint: notifications.error
        ? "Couldn't load alerts"
        : unread > 0
          ? `${unread} unread`
          : 'All caught up',
      ...TILE_STYLES.notifications,
    },
  ];

  const choreLine = chores.error
    ? "We couldn't load your chores just now — the card below will try again."
    : todaysChores.length > 0
      ? `You have ${todaysChores.length} chore${todaysChores.length === 1 ? '' : 's'} to do today.`
      : mine.length > 0
        ? `You have ${mine.length} chore${mine.length === 1 ? '' : 's'} on your list this week.`
        : 'No chores on your list right now.';

  const homeTiles: HomeTileDef[] = [
    { key: 'chores', label: 'Chores', defaultSize: 'wide', node: <KidChoresCard res={chores} memberId={memberId} color={color} /> },
    { key: 'allowance', label: 'Allowance', defaultSize: 'small', node: <AllowanceCard res={allowance} color={color} /> },
    { key: 'board', label: 'Board', defaultSize: 'tall', node: <MessageBoardCard /> },
    { key: 'dinner', label: 'Dinner', defaultSize: 'small', node: <DinnerCard res={plan} date={today} /> },
    { key: 'upnext', label: 'Up next', defaultSize: 'small', node: <UpNextCard res={events} /> },
    { key: 'weather', label: 'Weather', defaultSize: 'small', node: <WeatherCard /> },
    { key: 'birthdays', label: 'Birthdays', defaultSize: 'small', node: <BirthdaysCard /> },
    { key: 'kid_money', label: 'My money', defaultSize: 'wide', node: <KidMoneyCard /> },
    { key: 'ask', label: 'Ask', defaultSize: 'small', node: <AskCard /> },
    { key: 'verse', label: 'Verse', defaultSize: 'small', node: <VerseCard /> },
  ];

  return (
    <div className="space-y-4">
      <Hero name={identity.name ?? 'friend'} color={color}>
        {choreLine}
        {dinner ? ` Dinner tonight is ${dinner.name}.` : ''}
      </Hero>

      <HomeGrid tiles={homeTiles} />

      <ModuleTiles tiles={tiles} />
    </div>
  );
}
